import { useEffect, useState } from "react";

import {
  ArchiveBoxIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  InboxArrowDownIcon,
  PaperAirplaneIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import HeadlineStat from "../shared/HeadlineStat";
import OrderStatsTable from "./OrderStatsTable";
import apiConfig from "../../utils/apiConfig";
import classNames from "../../utils/classNames";
import useApiHelper from "../../hooks/useApiHelper";

export interface IUserPartDataStats {
  userId: string;
  userFullName: string;
  dispatched: number;
  dailyAvgDispatched: number;
  picked: number;
  dailyAvgPicked: number;
  packed: number;
  dailyAvgPacked: number;
  returnsRate: number;
  missingRate: number;
}

interface OrderStatsResponse {
  awaitingPicking?: number;
  awaitingPacking?: number;
  awaitingDispatch?: number;
  newInToday?: number;
  dispatchedToday?: number;
  overdue?: number;
  shippingStats?: Record<string, number>;
  userStats?: IUserPartDataStats[];
}

/** Friendly tint per carrier — keeps the shipping panel scannable at a
 * glance without having to read labels. Falls back to slate. */
const CARRIER_TONE: Record<string, { bar: string; dot: string; text: string }> =
  {
    royal: { bar: "bg-rose-400", dot: "bg-rose-500", text: "text-rose-700" },
    rm: { bar: "bg-rose-400", dot: "bg-rose-500", text: "text-rose-700" },
    dpd: { bar: "bg-red-400", dot: "bg-red-500", text: "text-red-700" },
    evri: { bar: "bg-emerald-400", dot: "bg-emerald-500", text: "text-emerald-700" },
    hermes: { bar: "bg-emerald-400", dot: "bg-emerald-500", text: "text-emerald-700" },
    parcelforce: { bar: "bg-sky-400", dot: "bg-sky-500", text: "text-sky-700" },
    ups: { bar: "bg-amber-500", dot: "bg-amber-500", text: "text-amber-700" },
    fedex: { bar: "bg-violet-400", dot: "bg-violet-500", text: "text-violet-700" },
    dhl: { bar: "bg-yellow-400", dot: "bg-yellow-500", text: "text-yellow-700" },
    yodel: { bar: "bg-indigo-400", dot: "bg-indigo-500", text: "text-indigo-700" },
    collect: { bar: "bg-slate-400", dot: "bg-slate-500", text: "text-slate-700" },
    collection: { bar: "bg-slate-400", dot: "bg-slate-500", text: "text-slate-700" },
    pickup: { bar: "bg-slate-400", dot: "bg-slate-500", text: "text-slate-700" },
  };

function toneForCarrier(name: string) {
  const key = name.toLowerCase();
  for (const k of Object.keys(CARRIER_TONE)) {
    if (key.includes(k)) return CARRIER_TONE[k];
  }
  return {
    bar: "bg-primary-400",
    dot: "bg-primary-500",
    text: "text-primary-700",
  };
}

export default function EbayOrdersStats({ pageMode }: { pageMode?: number }) {
  const { post } = useApiHelper(apiConfig("EbayOrdersStats"));

  const [providers, setProviders] = useState<Record<string, number>>({});
  const [awaitingPicking, setAwaitingPicking] = useState(0);
  const [awaitingPacking, setAwaitingPacking] = useState(0);
  const [awaitingDispatch, setAwaitingDispatch] = useState(0);
  const [newInToday, setNewInToday] = useState(0);
  const [dispatchedToday, setDispatchedToday] = useState(0);
  const [overdue, setOverdue] = useState(0);
  const [userStats, setUserStats] = useState<IUserPartDataStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    post<{}, { data: OrderStatsResponse }>(`/part/orders/stats`, {})
      .then((response) => {
        const data = response?.data || {};
        setAwaitingPicking(data.awaitingPicking ?? 0);
        setAwaitingPacking(data.awaitingPacking ?? 0);
        setAwaitingDispatch(data.awaitingDispatch ?? 0);
        setNewInToday(data.newInToday ?? 0);
        setDispatchedToday(data.dispatchedToday ?? 0);
        setOverdue(data.overdue ?? 0);
        setProviders(data.shippingStats ?? {});
        setUserStats(data.userStats ?? []);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const totalOpen = awaitingPicking + awaitingPacking + awaitingDispatch;
  const providerEntries = Object.entries(providers).sort(
    ([, a], [, b]) => b - a
  );
  const providerTotal = providerEntries.reduce((sum, [, n]) => sum + n, 0);
  const providerMax = providerEntries.reduce((m, [, n]) => Math.max(m, n), 0);

  return (
    <Card
      title="Orders overview"
      bodyClassName="p-4 space-y-4"
      collapsible
    >
      {/* Headline stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <HeadlineStat
          value={awaitingPicking.toLocaleString("en-GB")}
          label="Awaiting picking"
          sublabel={totalOpen ? `${Math.round((awaitingPicking / Math.max(1, totalOpen)) * 100)}% of open` : "Ready to pick"}
          tone="cyan"
          icon={ClipboardDocumentCheckIcon}
        />
        <HeadlineStat
          value={awaitingPacking.toLocaleString("en-GB")}
          label="Awaiting packing"
          sublabel="Picked, waiting to box"
          tone="violet"
          icon={ArchiveBoxIcon}
        />
        <HeadlineStat
          value={awaitingDispatch.toLocaleString("en-GB")}
          label="Awaiting dispatch"
          sublabel="Boxed, waiting for carrier"
          tone="amber"
          icon={ArrowUpTrayIcon}
        />
        <HeadlineStat
          value={newInToday.toLocaleString("en-GB")}
          label="New in today"
          sublabel="Orders received"
          tone="blue"
          icon={InboxArrowDownIcon}
        />
        <HeadlineStat
          value={dispatchedToday.toLocaleString("en-GB")}
          label="Dispatched today"
          sublabel="Handed to carrier"
          tone="emerald"
          icon={PaperAirplaneIcon}
        />
        <HeadlineStat
          value={overdue.toLocaleString("en-GB")}
          label="Overdue"
          sublabel="Of priced parts"
          tone="rose"
          icon={ExclamationTriangleIcon}
        />
      </div>

      {/* Shipping + user performance */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Shipping providers — sleek horizontal bars showing relative
            volume across carriers for the window. */}
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <header className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <TruckIcon className="h-4 w-4" aria-hidden />
              </span>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Shipping carriers
                </h3>
                <p className="text-[11px] text-slate-500">
                  Open orders by chosen service
                </p>
              </div>
            </div>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
              {providerTotal.toLocaleString("en-GB")}
            </span>
          </header>

          {providerEntries.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
              {isLoading
                ? "Loading carriers…"
                : "No outstanding shipments."}
            </div>
          ) : (
            <ul className="space-y-2.5">
              {providerEntries.map(([name, count]) => {
                const tone = toneForCarrier(name);
                const pct = providerMax === 0 ? 0 : (count / providerMax) * 100;
                const share =
                  providerTotal === 0
                    ? 0
                    : Math.round((count / providerTotal) * 100);
                return (
                  <li key={name} className="space-y-1">
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span
                          className={classNames(
                            "h-1.5 w-1.5 flex-none rounded-full",
                            tone.dot
                          )}
                          aria-hidden
                        />
                        <span className="truncate font-medium text-slate-700">
                          {name}
                        </span>
                      </span>
                      <span className="flex flex-none items-baseline gap-1 tabular-nums">
                        <span className="font-semibold text-slate-900">
                          {count.toLocaleString("en-GB")}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {share}%
                        </span>
                      </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={classNames(
                          "h-full rounded-full transition-[width] duration-500",
                          tone.bar
                        )}
                        style={{ width: `${Math.max(pct, 4)}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* User performance — colour-banded columns per action (dispatched
            / picked / packed) keep wide tables readable. */}
        <section className="lg:col-span-2">
          <OrderStatsTable orderStats={userStats} isLoading={isLoading} />
        </section>
      </div>
    </Card>
  );
}
